import WidgetHelpers from "helpers/WidgetHelpers/WidgetHelpers";
import { useMemo } from "react";
import { mergeSx } from "utils/mergeSx";
import BadgeDeltaContainer from "./components/BadgeDeltaContainer";
import { DeltaTypes } from "types/delta-types";
import type { ChipProps } from "@mui/material/Chip";
import type { DeltaType } from "types/delta-types";

export interface BadgeDeltaProps extends Omit<ChipProps, "children" | "label"> {
  children: React.ReactNode;
  // string
  // Default: sm
  // Set the component's width behavior.
  size?: "small" | "medium";
  // string
  // Default: increase
  // Perceived degree of increase or decrease of delta value.
  deltaType: DeltaType;
  // boolean
  // Default: { true }
  // Indicate wether positive change is interpreted as positive.
  isIncreasePositive?: boolean;
}

function BadgeDelta(props: BadgeDeltaProps) {
  const { deltaType = DeltaTypes.Increase, isIncreasePositive = true, size = "medium", children, sx, ...rest } = props;

  const Icon = useMemo(() => WidgetHelpers.getDeltaTypeIcons(deltaType), [deltaType]);
  const mappedDeltaType = useMemo(
    () => WidgetHelpers.mapInputsToDeltaType(deltaType, isIncreasePositive),
    [deltaType, isIncreasePositive]
  );
  const badgeColors = useMemo(() => WidgetHelpers.getDeltaTypeStyles(mappedDeltaType), [mappedDeltaType]);

  const icon = useMemo(
    () =>
      Icon ? (
        <Icon fontSize="inherit" style={{ color: badgeColors.bgColor, fill: badgeColors.iconColor }} />
      ) : undefined,
    [Icon, badgeColors.bgColor, badgeColors.iconColor]
  );

  const mergedSx = useMemo(
    () =>
      mergeSx(
        {
          backgroundColor: badgeColors.bgColor,
          color: badgeColors.textColor,

          "&:hover": {
            backgroundColor: badgeColors.bgColor,
            color: badgeColors.textColor,
          },
        },
        sx
      ),
    [badgeColors.bgColor, badgeColors.textColor, sx]
  );

  return (
    <BadgeDeltaContainer
      data-testid="badge-delta"
      size={size}
      icon={icon}
      clickable={false}
      label={children}
      {...rest}
      // Merged so a caller's sx overrides the delta colours without dropping them when it is undefined.
      sx={mergedSx}
    />
  );
}

export default BadgeDelta;
